import React from 'react';
import {
  Scale,
  Shield,
  Gavel,
  Star,
  Briefcase,
  Heart,
  Users,
  Ban,
  CheckCircle,
  FileText,
  PenLine,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CodeOfConductPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/40 via-white to-pink-50/30">
      <Header />

      <div className="max-w-5xl mx-auto px-4 py-12 pt-24">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-10">
          <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
            <Scale className="w-7 h-7 text-purple-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-purple-700 mb-3">
            Code of Conduct
          </h1>
          <p className="text-gray-500 text-sm mb-1">Last Updated: March 2026</p>
          <p className="text-gray-500 text-sm">
            Mandatory professional standards for all KoPartner users
          </p>
        </div>

        {/* Zero Tolerance */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 md:p-8 mb-8 flex gap-3">
          <Gavel className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
          <div>
            <h2 className="text-2xl font-bold text-red-800 mb-3">
              ZERO TOLERANCE POLICY
            </h2>
            <p className="text-red-800 mb-3 leading-relaxed">
              KoPartner maintains STRICT ZERO TOLERANCE for any form of harassment,
              misconduct, illegal activities, sexual advances, romantic propositions,
              or violation of professional boundaries.
            </p>
            <p className="text-red-800 leading-relaxed">
              Violations will result in IMMEDIATE AND PERMANENT ACCOUNT TERMINATION
              without refund, forfeiture of all wallet balance and earnings, and may be
              reported to law enforcement authorities under applicable Indian laws
              including IPC Sections 354, 354A, 354D, 506, and IT Act provisions.
            </p>
          </div>
        </div>

        <div className="space-y-8">
          {/* Understanding Platform */}
          <section className="bg-white rounded-3xl shadow-sm p-6 md:p-8">
            <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-purple-700 mb-4 flex items-center gap-3">
                <Shield className="w-6 h-6 text-purple-600" />
                Understanding KoPartner Platform
              </h2>
              <p className="text-purple-900 text-lg leading-relaxed mb-6">
                KoPartner is India&apos;s #1 Professional Social Support Services Platform
                providing STRICTLY PROFESSIONAL services across all Indian pin codes.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-2xl p-5">
                  <h3 className="text-xl font-bold text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    KoPartner IS:
                  </h3>
                  <ul className="space-y-2 text-green-800">
                    <li>• Professional social support platform</li>
                    <li>• Verified companion service for assistance</li>
                    <li>• Elder care and medical support provider</li>
                    <li>• Shopping and errand assistance</li>
                    <li>• Event and activity companion service</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-2xl p-5">
                  <h3 className="text-xl font-bold text-red-800 mb-3 flex items-center gap-2">
                    <Ban className="w-5 h-5" />
                    KoPartner is NOT:
                  </h3>
                  <ul className="space-y-2 text-red-800">
                    <li>• Dating or matchmaking service</li>
                    <li>• Matrimonial platform</li>
                    <li>• Escort or adult services</li>
                    <li>• Romantic relationship facilitator</li>
                    <li>• Platform for any illegal activities</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Fundamental Principles */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Star className="w-6 h-6 text-amber-500" />
              Fundamental Principles
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-green-800 mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Professionalism
                </h3>
                <p className="text-green-800 leading-relaxed">
                  All interactions must be strictly professional at ALL times.
                  Maintain appropriate boundaries. This is a business relationship,
                  not a personal one.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  Consent First
                </h3>
                <p className="text-blue-800 leading-relaxed">
                  Respect boundaries and consent absolutely. &quot;No&quot; means
                  &quot;No&quot; - no exceptions, no negotiations. All services must be
                  explicitly agreed upon beforehand.
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-purple-800 mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Safety Priority
                </h3>
                <p className="text-purple-800 leading-relaxed">
                  Personal safety is paramount. Use SOS features if needed. Report
                  concerns immediately. Never compromise safety for any reason.
                </p>
              </div>

              <div className="bg-pink-50 border border-pink-200 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-pink-800 mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Mutual Respect
                </h3>
                <p className="text-pink-800 leading-relaxed">
                  Treat all users with dignity and respect regardless of background,
                  religion, caste, gender, or any other factor. Zero tolerance for
                  discrimination.
                </p>
              </div>
            </div>
          </section>

          {/* KoPartner Service Providers */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Users className="w-6 h-6 text-purple-500" />
              Mandatory Code for KoPartner Service Providers
            </h2>

            <div className="bg-green-50 rounded-2xl p-6 mb-4">
              <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                MANDATORY REQUIREMENTS:
              </h3>
              <ul className="space-y-3 text-green-800">
                <li>• Maintain professional demeanor and appearance at ALL times</li>
                <li>• Be punctual - arrive on time or earlier for all bookings</li>
                <li>• Dress appropriately for the service and venue</li>
                <li>• Communicate clearly, politely, and professionally</li>
                <li>• Provide accurate information about services and availability</li>
                <li>• Complete services as agreed - no shortcuts or early termination</li>
                <li>• Report suspicious or inappropriate behavior immediately</li>
                <li>• Use SOS feature if you feel unsafe at any point</li>
                <li>• Maintain complete confidentiality of client information</li>
                <li>• Follow all platform guidelines and policies strictly</li>
                <li>• Keep personal contact details private until necessary for service</li>
                <li>• Accept that you are an independent contractor, not an employee</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5" />
                STRICTLY PROHIBITED (RESULTS IN IMMEDIATE BAN):
              </h3>
              <ul className="space-y-3 text-red-800">
                <li>• <strong>Any romantic, sexual, or intimate services, advances, or suggestions</strong></li>
                <li>• <strong>Accepting or soliciting dates, personal relationships, or marriage proposals</strong></li>
                <li>• <strong>Physical contact without explicit consent (except as required for service like elder care assistance)</strong></li>
                <li>• Consuming alcohol or drugs during or before service</li>
                <li>• Engaging in any illegal activities whatsoever</li>
                <li>• Sharing client information with anyone</li>
                <li>• Discrimination based on religion, caste, gender, appearance, or any factor</li>
                <li>• Providing false or misleading information</li>
                <li>• Bypassing platform to receive direct payments</li>
                <li>• Using offensive, abusive, or inappropriate language</li>
                <li>• Photography or recording without explicit permission</li>
                <li>• Soliciting tips or additional payments outside platform</li>
                <li>• Cancelling bookings without valid reason</li>
                <li>• Creating multiple accounts or fake profiles</li>
              </ul>
            </div>
          </section>

          {/* Clients */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Heart className="w-6 h-6 text-pink-500" />
              Mandatory Code for Clients
            </h2>

            <div className="bg-green-50 rounded-2xl p-6 mb-4">
              <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                MANDATORY REQUIREMENTS:
              </h3>
              <ul className="space-y-3 text-green-800">
                <li>• Treat KoPartners with respect and dignity at all times</li>
                <li>• Provide accurate booking information</li>
                <li>• Be present and punctual for scheduled services</li>
                <li>• Communicate requirements clearly and professionally</li>
                <li>• Respect professional boundaries absolutely</li>
                <li>• Provide a safe environment for the KoPartner</li>
                <li>• Pay through the platform as agreed</li>
                <li>• Provide honest reviews and feedback</li>
                <li>• Report concerns through official channels only</li>
                <li>• Understand that KoPartner selection is your responsibility</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5" />
                STRICTLY PROHIBITED (RESULTS IN IMMEDIATE BAN):
              </h3>
              <ul className="space-y-3 text-red-800">
                <li>• <strong>Making romantic, sexual, or dating advances toward KoPartners</strong></li>
                <li>• <strong>Requesting services not offered on the platform</strong></li>
                <li>• <strong>Offering additional money for &quot;extra services&quot;</strong></li>
                <li>• <strong>Any form of physical contact without explicit consent</strong></li>
                <li>• Harassment, stalking, or intimidation of any kind</li>
                <li>• Providing false or misleading information</li>
                <li>• Attempting to bypass platform payments</li>
                <li>• Recording or photographing without permission</li>
                <li>• Abusive, offensive, or threatening behavior</li>
                <li>• Discrimination of any kind</li>
                <li>• Attempting personal contact outside the platform</li>
                <li>• Making false reports or complaints</li>
                <li>• Creating multiple accounts</li>
              </ul>
            </div>
          </section>

          {/* Safety Guidelines */}
          <section className="bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-amber-800 mb-5 flex items-center gap-3">
              <Shield className="w-6 h-6 text-amber-700" />
              Safety Guidelines for All Users
            </h2>
            <ul className="space-y-3 text-amber-900">
              <li>• First meetings should be in public places when possible</li>
              <li>• Share your location with a trusted person</li>
              <li>• Keep your phone charged and accessible</li>
              <li>• Use the in-app SOS button immediately if you feel unsafe</li>
              <li>• Trust your instincts - if something feels wrong, leave immediately</li>
              <li>• Report suspicious behavior through official channels</li>
              <li>• Never share personal financial information</li>
              <li>• Verify identity through the app before meeting</li>
              <li>• Do not consume substances that impair judgment</li>
              <li>• Keep communication through the platform until necessary</li>
            </ul>
          </section>

          {/* Consequences */}
          <section className="bg-red-50 border border-red-200 rounded-2xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-red-800 mb-4 flex items-center gap-3">
              <Gavel className="w-6 h-6 text-red-700" />
              Consequences of Violations
            </h2>
            <p className="text-red-900 font-semibold mb-5">
              Violations of this Code of Conduct will result in:
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-amber-50 rounded-xl p-5">
                <h3 className="text-xl font-bold text-amber-800 mb-3">Minor Violations</h3>
                <ul className="space-y-2 text-amber-800">
                  <li>• First offense: Written warning</li>
                  <li>• Second offense: Temporary suspension</li>
                  <li>• Third offense: Permanent ban</li>
                </ul>
              </div>

              <div className="bg-red-100 rounded-xl p-5">
                <h3 className="text-xl font-bold text-red-800 mb-3">Major Violations</h3>
                <ul className="space-y-2 text-red-800">
                  <li>• Immediate permanent ban</li>
                  <li>• Forfeiture of all balances/earnings</li>
                  <li>• No refunds of any kind</li>
                  <li>• Report to law enforcement</li>
                </ul>
              </div>
            </div>

            <p className="text-red-900 font-semibold mb-3">
              Major violations include but are not limited to:
            </p>
            <ul className="space-y-2 text-red-900 mb-5">
              <li>• Sexual harassment or advances</li>
              <li>• Physical assault or threats</li>
              <li>• Stalking or intimidation</li>
              <li>• Fraud or financial crimes</li>
              <li>• Any illegal activities</li>
              <li>• Sharing explicit content</li>
              <li>• Identity theft or impersonation</li>
            </ul>

            <div className="bg-red-200/70 rounded-xl p-5">
              <p className="text-red-900 font-semibold leading-relaxed">
                KoPartner reserves the right to terminate accounts without prior notice for
                serious violations and cooperate fully with law enforcement investigations.
                All decisions regarding violations are final and not subject to appeal.
              </p>
            </div>
          </section>

          {/* Reporting */}
          <section className="bg-white rounded-2xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <FileText className="w-6 h-6 text-purple-600" />
              Reporting Violations
            </h2>
            <p className="text-gray-700 mb-4">
              If you witness or experience any violation:
            </p>

            <ol className="space-y-3 text-gray-800 mb-5">
              <li>1. <strong>Immediate Safety Concern:</strong> Use the SOS button and/or call local police</li>
              <li>2. <strong>In-App Report:</strong> Use the reporting feature immediately</li>
              <li>3. <strong>Email:</strong> Send details to conduct@kopartner.in or safety@kopartner.in</li>
              <li>4. <strong>Document:</strong> Capture screenshots if possible and safe to do so</li>
            </ol>

            <p className="text-gray-700 leading-relaxed">
              All reports are investigated within 24 hours. Reports are handled with strict
              confidentiality. Retaliation against those who report violations is strictly
              prohibited and will result in immediate termination.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 rounded-2xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Contact Information
            </h2>
            <p className="font-bold text-gray-800">SET INDIA BUSINESS PVT LTD</p>
            <p className="text-gray-700">Conduct Issues: conduct@kopartner.in</p>
            <p className="text-gray-700">Safety Concerns: safety@kopartner.in</p>
            <p className="text-gray-700">Grievance Officer: grievance@kopartner.in</p>
            <p className="text-gray-700">Website: www.kopartner.in</p>
          </section>

          {/* Acknowledgment */}
          <section className="bg-purple-50 border border-purple-200 rounded-2xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-purple-700 mb-4 flex items-center gap-3">
              <PenLine className="w-6 h-6 text-purple-600" />
              Acknowledgment and Agreement
            </h2>
            <p className="text-purple-900 uppercase leading-relaxed">
              By using KoPartner, you acknowledge that you have read, understood,
              and agree to abide by this Code of Conduct in its entirety. You understand
              that violations may result in immediate account termination without refund
              and potential legal action under applicable Indian laws including the
              Indian Penal Code and Information Technology Act, 2000.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CodeOfConductPage;
